import React from 'react';
import './FmWorkspaceShell.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export interface FmWorkspaceShellProps {
  /** Sticky header region (topbar / breadcrumbs). */
  header?: React.ReactNode;
  /** Sticky footer region (back / reset / primary action). */
  footer?: React.ReactNode;
  /** Scrollable content column. */
  children: React.ReactNode;
  /** Passthrough test id for the shell root. */
  rootTestId?: string;
  /** Passthrough test id for the header region. */
  headerTestId?: string;
  /** Passthrough test id for the scrollable content column. */
  contentTestId?: string;
  /** Passthrough test id for the footer region. */
  footerTestId?: string;
  className?: string;
}

/**
 * FmWorkspaceShell.FooterItem — wraps a footer action to maintain responsive
 * clamping on mobile (min-width: 0, max-width constraint). Use for buttons,
 * controls, and other small discrete footer elements.
 */
export const FooterItem: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cx('fm-workspace-shell__footer-item', className)}>{children}</div>;

/**
 * FmWorkspaceShell.FooterHiddenOnMobile — wraps a footer region to hide it on
 * mobile (display: none below tablet breakpoint). Use for secondary/conditional footer
 * content (e.g., selective reset actions).
 */
export const FooterHiddenOnMobile: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cx('fm-workspace-shell__footer-hide-mobile', className)}>{children}</div>;

/**
 * FmWorkspaceShell — reusable E2 shell anatomy: a sticky header region, a
 * single scrollable content column, and a sticky footer. Domain-neutral
 * replacement for the former StandDetailShell markup + CSS. Layout lives in
 * FmWorkspaceShell.css (DS-owned primitive CSS, tokens only).
 *
 * Header region: always rendered (even when empty) to preserve test id contract
 * for specs that assert shell anatomy. The header slot receives optional content
 * (typically null when shell-level chrome owns the layout).
 */
export const FmWorkspaceShell: React.FC<FmWorkspaceShellProps> & {
  FooterItem: typeof FooterItem;
  FooterHiddenOnMobile: typeof FooterHiddenOnMobile;
} = ({
  header,
  footer,
  children,
  rootTestId,
  headerTestId,
  contentTestId,
  footerTestId,
  className,
}) => (
  <div className={cx('fm-workspace-shell', className)} data-testid={rootTestId}>
    <header className="fm-workspace-shell__header" data-testid={headerTestId}>
      {header}
    </header>
    <div className="fm-workspace-shell__content" data-testid={contentTestId}>
      {children}
    </div>
    {footer ? (
      <footer className="fm-workspace-shell__footer" data-testid={footerTestId}>
        {footer}
      </footer>
    ) : null}
  </div>
);

// Attach sub-components
FmWorkspaceShell.FooterItem = FooterItem;
FmWorkspaceShell.FooterHiddenOnMobile = FooterHiddenOnMobile;

export default FmWorkspaceShell;
